import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import './ModernNavbar.css';

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

interface SiteSettings {
  siteName: string;
  logoUrl: string;
  accentColor: string;
}

const ModernNavbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    siteName: 'NortiqBlog',
    logoUrl: '',
    accentColor: '#fa5d5d'
  });
  const [categories, setCategories] = useState<Category[]>([]);

  const { currentUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const categoriesRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const loadCategories = async () => {
    try {
      if (!isFirebaseConfigured || !db) return;
      
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];
      
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSiteSettings = async () => {
    try {
      if (!isFirebaseConfigured || !db) return;
      
      const settingsDoc = await getDoc(doc(db, 'siteSettings', 'main'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data() as SiteSettings;
        setSiteSettings(prev => ({ ...prev, ...settings }));
      }
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  useEffect(() => {
    loadCategories();
    loadSiteSettings();
  }, [currentUser]); // Reload when authentication state changes

  // Apply accent color from dashboard settings
  useEffect(() => {
    if (siteSettings.accentColor) {
      document.documentElement.style.setProperty('--navbar-accent-color', siteSettings.accentColor);
      document.documentElement.style.setProperty('--navbar-accent-hover', `${siteSettings.accentColor}15`);
      document.documentElement.style.setProperty('--navbar-accent-light', `${siteSettings.accentColor}10`);
    }
  }, [siteSettings.accentColor]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsUserMenuOpen(false);
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const closeAllMenus = () => {
    setIsMenuOpen(false);
    setIsCategoriesOpen(false);
    setIsUserMenuOpen(false);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoriesRef.current && !categoriesRef.current.contains(event.target as Node)) {
        setIsCategoriesOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    closeAllMenus();
  }, [location.pathname]);

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="modern-navbar">
      <div className="modern-navbar-container">
        {/* Logo */}
        <Link to="/" className="modern-navbar-logo animate-slide-in-left">
          <img 
            src="/logo.png" 
            alt={siteSettings.siteName}
            className="modern-logo-image"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="modern-navbar-menu">
          <Link 
            to="/" 
            className={`modern-nav-link ${isActiveRoute('/') ? 'active' : ''}`}
          >
            Home
          </Link>

          {/* Categories Dropdown */}
          <div className="modern-dropdown" ref={categoriesRef}>
            <button
              className="modern-nav-link modern-dropdown-trigger"
              onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
            >
              Categories
              <ChevronDown size={16} className={`dropdown-icon ${isCategoriesOpen ? 'rotated' : ''}`} />
            </button>
            
            {isCategoriesOpen && (
              <div className="modern-dropdown-menu animate-fade-in">
                <div className="dropdown-section">
                  <div className="dropdown-title">Browse Categories</div>
                  {categories.length > 0 ? (
                    categories.map((category, index) => (
                      <Link
                        key={category.id}
                        to={`/category/${category.slug}`}
                        className="dropdown-item animate-fade-in-up animate-stagger-1"
                        onClick={closeAllMenus}
                      >
                        <span className="dropdown-item-name">{category.name}</span>
                        {category.description && (
                          <span className="dropdown-item-desc">{category.description}</span>
                        )}
                      </Link>
                    ))
                  ) : (
                    <div className="dropdown-empty">No categories available</div>
                  )}
                </div>
                <div className="dropdown-divider"></div>
                <div className="dropdown-section">
                  <div className="dropdown-title">Popular Sections</div>
                  <Link to="/featured" className="dropdown-item" onClick={closeAllMenus}>
                    <span className="dropdown-item-name">Featured Posts</span>
                    <span className="dropdown-item-desc">Our top recommended content</span>
                  </Link>
                  <Link to="/popular" className="dropdown-item" onClick={closeAllMenus}>
                    <span className="dropdown-item-name">Popular Posts</span>
                    <span className="dropdown-item-desc">Most viewed articles</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="modern-search animate-slide-in-right">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="modern-search-input"
            />
            <button type="submit" className="modern-search-button">
              <Search size={18} />
            </button>
          </div>
        </form>

        {/* User Menu / Auth */}
        <div className="modern-navbar-actions">
          {currentUser ? (
            <div className="modern-user-menu" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="modern-user-button"
              >
                <User size={20} />
              </button>
              
              {isUserMenuOpen && (
                <div className="modern-user-dropdown animate-fade-in">
                  <div className="user-dropdown-header">
                    <div className="user-info">
                      <span className="user-email">{currentUser.email}</span>
                    </div>
                  </div>
                  <div className="user-dropdown-divider"></div>
                  <Link to="/profile" className="user-dropdown-item" onClick={closeAllMenus}>
                    <User size={16} />
                    Profile
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="user-dropdown-item" onClick={closeAllMenus}>
                      <Settings size={16} />
                      Admin Dashboard
                    </Link>
                  )}
                  <div className="user-dropdown-divider"></div>
                  <button onClick={handleLogout} className="user-dropdown-item logout">
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="modern-auth-buttons animate-slide-in-right">
              <Link to="/login" className="modern-btn modern-btn-ghost">
                Login
              </Link>
              <Link to="/signup" className="modern-btn modern-btn-primary">
                Sign Up
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="modern-mobile-toggle"
          aria-label="Toggle mobile menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="modern-mobile-menu animate-fade-in">
          {/* Mobile Search */}
          <div className="mobile-search-section">
            <form onSubmit={handleSearch}>
              <div className="mobile-search-wrapper">
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mobile-search-input"
                />
                <button type="submit" className="mobile-search-button">
                  <Search size={20} />
                </button>
              </div>
            </form>
          </div>

          {/* Mobile Navigation Links */}
          <div className="mobile-nav-section">
            <div className="mobile-section-title">Navigation</div>
            <Link 
              to="/" 
              className={`mobile-nav-link ${isActiveRoute('/') ? 'active' : ''}`}
              onClick={closeAllMenus}
            >
              Home
            </Link>
          </div>

          {/* Mobile Popular Sections */}
          <div className="mobile-nav-section">
            <div className="mobile-section-title">Popular Sections</div>
            <Link 
              to="/featured" 
              className={`mobile-nav-link mobile-category-link ${isActiveRoute('/featured') ? 'active' : ''}`}
              onClick={closeAllMenus}
            >
              Featured Posts
            </Link>
            <Link 
              to="/popular" 
              className={`mobile-nav-link mobile-category-link ${isActiveRoute('/popular') ? 'active' : ''}`}
              onClick={closeAllMenus}
            >
              Popular Posts
            </Link>
          </div>

          {/* Mobile Categories */}
          {categories.length > 0 && (
            <div className="mobile-nav-section">
              <div className="mobile-section-title">Categories</div>
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.slug}`}
                  className="mobile-nav-link mobile-category-link"
                  onClick={closeAllMenus}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          )}

          {/* Mobile User Section */}
          {currentUser ? (
            <div className="mobile-nav-section mobile-user-section">
              <div className="mobile-user-info">
                <User size={20} />
                <span>{currentUser.email}</span>
              </div>
              <Link to="/profile" className="mobile-nav-link" onClick={closeAllMenus}>
                Profile
              </Link>
              {isAdmin && (
                <Link to="/admin" className="mobile-nav-link" onClick={closeAllMenus}>
                  Admin Dashboard
                </Link>
              )}
              <button onClick={handleLogout} className="mobile-nav-link mobile-logout">
                <LogOut size={16} />
                Logout
              </button>
            </div>
          ) : (
            <div className="mobile-nav-section mobile-auth-section">
              <Link to="/login" className="modern-btn modern-btn-ghost mobile-auth-btn" onClick={closeAllMenus}>
                Login
              </Link>
              <Link to="/signup" className="modern-btn modern-btn-primary mobile-auth-btn" onClick={closeAllMenus}>
                Sign Up
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default ModernNavbar;
